import { RefObject } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Search, Sparkles, Loader2, Check } from "lucide-react";
import { TruncatedText } from "@/components/ui/truncated-text";
import type { KeywordSuggestion } from "@/resources/keyword-theme";
import { formatMetricNumber, NA_LABEL } from "@/utils/keywordMetricDisplay";
import type { Keyword } from "@/types/keyword";
import { RankBadge, SelectedKeywordChip, TrendCell, KeywordMetricValue } from "./KeywordThemeShared";
import { ThemeNameAutocomplete } from "./ThemeNameAutocomplete";

interface ManualThemeTabProps {
  name: string;
  onNameChange: (value: string) => void;
  showNameDropdown: boolean;
  onShowNameDropdownChange: (show: boolean) => void;
  nameSuggestions: Keyword[];
  stagedName: string | null;
  onStage: (value?: string, keywordId?: number) => void;
  onUnstage: () => void;
  nameInputRef: RefObject<HTMLInputElement>;

  selectedKeywords: Keyword[];
  selectedIds: Set<number>;
  onToggleId: (id: number) => void;

  kwSearch: string;
  onKwSearchChange: (value: string) => void;
  kwResults: Keyword[];

  isAskingAI: boolean;
  aiKwSuggestions: KeywordSuggestion[] | null;
  keywordById: Map<number, Keyword>;
  onAskAIKeywords: () => void;
  onAddAllSuggestions: () => void;
}

export function ManualThemeTab({
  name,
  onNameChange,
  showNameDropdown,
  onShowNameDropdownChange,
  nameSuggestions,
  stagedName,
  onStage,
  onUnstage,
  nameInputRef,
  selectedKeywords,
  selectedIds,
  onToggleId,
  kwSearch,
  onKwSearchChange,
  kwResults,
  isAskingAI,
  aiKwSuggestions,
  keywordById,
  onAskAIKeywords,
  onAddAllSuggestions,
}: ManualThemeTabProps) {
  return (
    <div className="space-y-5">
      {/* Step 1: theme name */}
      <ThemeNameAutocomplete
        name={name}
        onNameChange={onNameChange}
        showDropdown={showNameDropdown}
        onShowDropdownChange={onShowNameDropdownChange}
        nameSuggestions={nameSuggestions}
        stagedName={stagedName}
        onStage={onStage}
        onUnstage={onUnstage}
        nameInputRef={nameInputRef}
      />

      {/* Step 2: add keywords (only after name is staged) */}
      {stagedName && (
        <div className="border-t pt-5 space-y-4">

          {/* Section header */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-base">Add keywords</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Search your keyword bank, or let Signal AI suggest keywords for{" "}
                <span className="font-medium text-foreground">"{stagedName}"</span>.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onAskAIKeywords}
              disabled={isAskingAI}
              className="shrink-0 gap-1.5"
            >
              {isAskingAI ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5 text-primary" />
              )}
              {aiKwSuggestions !== null ? "Re-generate" : "Ask Signal AI"}
            </Button>
          </div>

          {/* Selected keywords chips */}
          {selectedKeywords.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">
                {selectedKeywords.length} keyword{selectedKeywords.length !== 1 ? "s" : ""} selected
              </p>
              <div className="flex flex-wrap gap-1.5">
                {selectedKeywords.map((kw) => (
                  <SelectedKeywordChip key={kw.id} label={kw.search} onRemove={() => onToggleId(kw.id)} />
                ))}
              </div>
            </div>
          )}

          {/* Keyword bank search */}
          <div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                value={kwSearch}
                onChange={(e) => onKwSearchChange(e.target.value)}
                placeholder='Search keyword bank… (use "quotes" for an exact match)'
                className="pl-8"
              />
            </div>
            {kwResults.length > 0 && (
              <div className="mt-1 border rounded-md overflow-hidden">
                <div className="grid grid-cols-[1fr_52px_44px_72px_48px_56px] gap-x-2 px-3 py-1.5 bg-muted/30 border-b">
                  {["Keyword", "Rank", "KPS", "Search Vol.", "Rel.", "Trend"].map((h) => (
                    <span key={h} className="text-xs font-normal text-muted-foreground">{h}</span>
                  ))}
                </div>
                {kwResults.map((kw) => {
                  const selected = selectedIds.has(kw.id);
                  return (
                    <div
                      key={kw.id}
                      onClick={() => onToggleId(kw.id)}
                      className={cn(
                        "grid grid-cols-[1fr_52px_44px_72px_48px_56px] gap-x-2 items-center px-3 py-2 border-b border-border/30 last:border-b-0 cursor-pointer select-none transition-colors",
                        selected ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-muted/30",
                      )}
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div className="w-3.5 shrink-0">
                          {selected && <Check className="h-3.5 w-3.5 text-primary" />}
                        </div>
                        <TruncatedText className="text-sm">{kw.search}</TruncatedText>
                      </div>
                      <div><RankBadge rank={kw.rank} emptyLabel={NA_LABEL} /></div>
                      <span className="text-xs text-center">
                        <KeywordMetricValue value={kw.kps} />
                      </span>
                      <span className="text-xs">
                        {kw.search_volume != null ? formatMetricNumber(kw.search_volume) : <span className="text-muted-foreground">—</span>}
                      </span>
                      <span className="text-xs text-center">
                        <KeywordMetricValue value={kw.hero_relevance} />
                      </span>
                      <TrendCell trend={kw.search_volume_trend} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* AI keyword suggestions */}
          {isAskingAI ? (
            <div className="flex items-center justify-center gap-2 py-6">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                Signal AI is finding keywords for this theme…
              </p>
            </div>
          ) : aiKwSuggestions !== null && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-semibold">Signal AI suggestions</span>
                </div>
                {aiKwSuggestions.length > 0 && (
                  <button
                    onClick={onAddAllSuggestions}
                    className="text-xs text-primary hover:underline"
                  >
                    Add all
                  </button>
                )}
              </div>
              {aiKwSuggestions.length === 0 ? (
                <p className="text-xs text-muted-foreground">No keyword suggestions found for this theme.</p>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <div className="grid grid-cols-[1fr_52px_44px_72px_48px_56px] gap-x-2 px-3 py-1.5 bg-muted/30 border-b">
                    {["Keyword", "Rank", "KPS", "Search Vol.", "Rel.", "Trend"].map((h) => (
                      <span key={h} className="text-xs font-normal text-muted-foreground">{h}</span>
                    ))}
                  </div>
                  {aiKwSuggestions.map((s) => {
                    const kw = keywordById.get(s.keyword_id);
                    const selected = selectedIds.has(s.keyword_id);
                    return (
                      <div
                        key={s.keyword_id}
                        onClick={() => onToggleId(s.keyword_id)}
                        className={cn(
                          "grid grid-cols-[1fr_52px_44px_72px_48px_56px] gap-x-2 items-center px-3 py-2 border-b border-border/30 last:border-b-0 cursor-pointer select-none transition-colors",
                          selected ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-muted/30",
                        )}
                      >
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-3.5 shrink-0">
                            {selected && <Check className="h-3.5 w-3.5 text-primary" />}
                          </div>
                          <div className="min-w-0">
                            <TruncatedText className="text-sm">{s.keyword}</TruncatedText>
                            {s.match_type && (
                              <TruncatedText className="text-[10px] text-muted-foreground">
                                {s.match_type.replace(/_/g, ' ')}
                              </TruncatedText>
                            )}
                          </div>
                        </div>
                        <div><RankBadge rank={kw?.rank ?? null} emptyLabel={NA_LABEL} /></div>
                        <span className="text-xs text-center">
                          <KeywordMetricValue value={kw?.kps} />
                        </span>
                        <span className="text-xs">
                          {kw?.search_volume != null ? formatMetricNumber(kw.search_volume) : <span className="text-muted-foreground">—</span>}
                        </span>
                        <span className="text-xs text-center">
                          <KeywordMetricValue value={kw?.hero_relevance} />
                        </span>
                        <TrendCell trend={kw?.search_volume_trend ?? null} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
